import LogRocket from 'logrocket';

import { store } from '@stores';
import { IntroTypes } from '@types';
import { stripQueryParams } from '@utils';

type LogRocketTrackProperties = NonNullable<
  Parameters<typeof LogRocket.track>[1]
>;

export class RudderstackService {
  private static _instance: RudderstackService;

  private constructor() {}

  static getInstance() {
    if (!RudderstackService._instance) {
      RudderstackService._instance = new RudderstackService();
    }
    return RudderstackService._instance;
  }

  private get baseParams() {
    const { partnerStore, initializeStore, formStore } = store;
    const moduleTitle = formStore.currentModule?.title ?? '';
    const pageTitle = moduleTitle
      ? `${document.title} - ${moduleTitle}`
      : document.title;

    return {
      partner_name: partnerStore.partner?.displayname ?? '',
      workflow_variant: initializeStore.getVariant(),
      page_title: pageTitle,
      host_source_page: initializeStore.options.requestingHostLong
        ? stripQueryParams(initializeStore.options.requestingHostLong)
        : '',
    };
  }

  private get partnerId(): string {
    return store.partnerStore.partner?.id ?? '';
  }

  private get programType(): string {
    return store.formStore.formData?.leadType ?? '';
  }

  private rudderstackTrack(
    eventName: string,
    properties: Record<string, unknown>
  ): void {
    window.rudderanalytics.track(eventName, properties, () => {
      console.warn(
        `Track event ${eventName} successfully submitted to the RudderStack SDK.`
      );
    });
  }

  private logRocketTrack(
    eventName: string,
    properties: Record<string, unknown>
  ): void {
    LogRocket.track(eventName, properties as LogRocketTrackProperties);
  }

  // Event: program_card_clicked
  // Triggered when: A user clicks one of the program cards (DS or DMP)
  programCardClicked = (programType: string): void => {
    const properties = {
      ...this.baseParams,
      partner_id: this.partnerId,
      program_type: programType,
    };
    this.rudderstackTrack('program_card_clicked', properties);
    this.logRocketTrack('program_card_clicked', properties);
  };

  // Event: plan_selected (was program_type in dataLayer)
  // Triggered when: A user selects a plan and clicks to go to SaaS
  planSelected = (programType: string): void => {
    const properties = {
      ...this.baseParams,
      partner_id: this.partnerId,
      program_type: programType,
    };
    this.rudderstackTrack('plan_selected', properties);
    this.logRocketTrack('plan_selected', properties);
  };

  // Event: pii_submitted (was contact_info_received in dataLayer)
  // Triggered when: A user submits their PII and we officially receive it
  piiSubmitted = (): void => {
    const properties = {
      ...this.baseParams,
      partner_id: this.partnerId,
    };
    this.rudderstackTrack('pii_submitted', properties);
    this.logRocketTrack('pii_submitted', properties);
  };

  // Event: review_responses_page_view (new event)
  // Triggered when: A user views the Review page
  reviewResponsesPageView = (): void => {
    this.rudderstackTrack('review_responses_page_view', this.baseParams);
  };

  // Event: completed_name_contact_info (new event)
  // Triggered when: A user clicks the button to submit their PII
  completedNameContactInfo = (): void => {
    this.rudderstackTrack('completed_name_contact_info', {
      ...this.baseParams,
      partner_id: this.partnerId,
      program_type: this.programType,
    });
  };

  // Event: multi_journey_reentry
  // Triggered when: A user submits the short form and their profile already exists
  multiJourneyReentry = (): void => {
    const properties = {
      ...this.baseParams,
      partner_id: this.partnerId,
    };
    this.rudderstackTrack('multi_journey_reentry', properties);
    this.logRocketTrack('multi_journey_reentry', properties);
  };

  // Event: language_toggle
  // Triggered when: A user toggles the language
  languageToggle = (): void => {
    this.rudderstackTrack('language_toggle', this.baseParams);
  };

  // Event: issue (was error_type in dataLayer)
  // Triggered when: An error is thrown
  issue = (issueType: string): void => {
    this.rudderstackTrack('issue', {
      ...this.baseParams,
      issue_type: issueType,
      program_type: this.programType,
    });
  };

  // Event: digital_enrollment_opted_in (was opted_for_digital_enrollment in dataLayer)
  // Triggered when: A user selects a plan and clicks to opt into digital enrollment
  digitalEnrollmentOptedIn = (programType: string): void => {
    const properties = {
      ...this.baseParams,
      partner_id: this.partnerId,
      program_type: programType,
    };
    this.rudderstackTrack('digital_enrollment_opted_in', properties);
    this.logRocketTrack('digital_enrollment_opted_in', properties);
  };

  // Event: digital_enrollment_offered (was offer_digital_enrollment in dataLayer)
  // Triggered when: A user is offered the option to enroll digitally
  digitalEnrollmentOffered = (): void => {
    const properties = {
      ...this.baseParams,
      partner_id: this.partnerId,
    };
    this.rudderstackTrack('digital_enrollment_offered', properties);
    this.logRocketTrack('digital_enrollment_offered', properties);
  };

  // Event: digital_enrollment_declined
  // Triggered when: A user declines to enroll digitally
  digitalEnrollmentDeclined = (programType: string): void => {
    const properties = {
      ...this.baseParams,
      partner_id: this.partnerId,
      program_type: programType,
    };
    this.rudderstackTrack('digital_enrollment_declined', properties);
    this.logRocketTrack('digital_enrollment_declined', properties);
  };

  // Event: button_click
  // Triggered when: A user clicks a button
  buttonClick = (clickText: string): void => {
    this.rudderstackTrack('button_click', {
      ...this.baseParams,
      click_text: clickText,
      program_type: this.programType,
    });
  };

  // Event: workflow_exit_prompt
  // Triggered when: A user clicks the X to open the early exit prompt
  workflowExitPrompt = (): void => {
    this.rudderstackTrack('workflow_exit_prompt', {
      ...this.baseParams,
      program_type: this.programType,
    });
  };

  // Event: workflow_exit_confirmed
  // Triggered when: A user clicks Exit Without Saving on the early exit prompt
  workflowExitConfirmed = (): void => {
    this.rudderstackTrack('workflow_exit_confirmed', {
      ...this.baseParams,
      program_type: this.programType,
    });
  };

  // Event: application_started (new event)
  // Triggered when: A user moves from the first screen to the second screen
  applicationStarted = (): void => {
    const properties = {
      ...this.baseParams,
      partner_id: this.partnerId,
    };
    this.rudderstackTrack('application_started', properties);
    this.logRocketTrack('application_started', properties);
  };

  // Event: application_completed (new event)
  // Triggered when: A user makes it through all pages and views their plan options
  applicationCompleted = (): void => {
    const properties = {
      ...this.baseParams,
      partner_id: this.partnerId,
    };
    this.rudderstackTrack('application_completed', properties);
    this.logRocketTrack('application_completed', properties);
  };

  // Event: app_initialized (was initialize in dataLayer)
  // Triggered when: The app initializes
  appInitialized = (
    initializationLanguage: string,
    introUI: IntroTypes | undefined
  ): void => {
    const properties = {
      ...this.baseParams,
      initialization_language: initializationLanguage,
      introUI: introUI || 'none',
      partner_id: this.partnerId,
    };
    this.rudderstackTrack('app_initialized', properties);
    this.logRocketTrack('app_initialized', properties);
  };

  // Event: graceful_rejection_prompt_closed
  // Triggered when: A user closes the graceful rejection prompt
  gracefulRejectionPromptClosed = (): void => {
    this.rudderstackTrack('graceful_rejection_prompt_closed', {
      ...this.baseParams,
      partner_id: this.partnerId,
    });
  };

  // Event: duplicate_lead_prompted
  // Triggered when: The duplicate lead issue-detected prompt is shown
  duplicateLeadPrompted = (): void => {
    const properties = {
      ...this.baseParams,
      partner_id: this.partnerId,
    };
    this.rudderstackTrack('duplicate_lead_prompted', properties);
    this.logRocketTrack('duplicate_lead_prompted', properties);
  };

  // Event: does_not_qualify_prompted
  // Triggered when: The does-not-qualify issue-detected prompt is shown
  doesNotQualifyPrompted = (): void => {
    const properties = {
      ...this.baseParams,
      partner_id: this.partnerId,
    };
    this.rudderstackTrack('does_not_qualify_prompted', properties);
    this.logRocketTrack('does_not_qualify_prompted', properties);
  };

  // Event: dnc_catch_all_prompted
  // Triggered when: The MAPI fallback (DNC catch-all) issue-detected prompt is shown
  dncCatchAllPrompted = (): void => {
    const properties = {
      ...this.baseParams,
      partner_id: this.partnerId,
    };
    this.rudderstackTrack('dnc_catch_all_prompted', properties);
    this.logRocketTrack('dnc_catch_all_prompted', properties);
  };
}

export const rudderstackService = RudderstackService.getInstance();
